class ChassisTabs extends HTMLElement {
  constructor() {
    super();
    this.buttons = this.querySelectorAll('.chassis-tabs__button');
    this.panels = this.querySelectorAll('.chassis-tabs__panel');
    
    // Get all containers
    this.specSheet = this.querySelector('.spec-sheet');
    this.datasheet = this.querySelector('.datasheet');
    this.gpuSupport = this.querySelector('.gpu-support');
    this.nvme = this.querySelector('.nvme');
    
    // Parse dynamic tabs
    this.dynamicTabsData = this.parseDynamicTabs();
    
    // DEBUG: Ver qué datos tenemos
    console.log('DEBUG - Dynamic Tabs Data:', this.dynamicTabsData);
    
    this.bindEvents();
    this.renderDynamicTabs();
  }

  parseDynamicTabs() {
    const allMetafieldsAttr = this.getAttribute('data-all-metafields');
    console.log('DEBUG - data-all-metafields:', allMetafieldsAttr);
    
    if (!allMetafieldsAttr) {
      console.log('DEBUG - No metafields found');
      return {};
    }
    
    let allMetafields;
    try {
      allMetafields = JSON.parse(allMetafieldsAttr);
      console.log('DEBUG - Parsed all metafields:', allMetafields);
    } catch (error) {
      console.error('DEBUG - Error parsing metafields JSON:', error);
      console.log('DEBUG - Raw JSON string:', allMetafieldsAttr);
      return {};
    }
    
    const tabsData = {};
    
    // Iterar sobre todos los metafields y filtrar los que terminan en _tab o _tab_número
    Object.keys(allMetafields).forEach(metafieldKey => {
      console.log(`DEBUG - Checking metafield: ${metafieldKey}`);
      
      // Verificar si termina en _tab o _tab_número
      if (metafieldKey.endsWith('_tab') || /.*_tab_\d+$/.test(metafieldKey)) {
        const content = allMetafields[metafieldKey];
        
        console.log(`DEBUG - Found tab metafield: ${metafieldKey} with content:`, content);
        
        // Verificaciones más estrictas para contenido válido
        if (this.isValidMetafieldContent(content)) {
          // Generate tab name from metafield key
          const tabName = this.generateTabName(metafieldKey);
          const tabId = this.generateTabId(metafieldKey);
          
          // Extraer el orden numérico si existe
          const orderNumber = this.extractOrderNumber(metafieldKey);
          
          // Parse the content
          const parsedContent = this.parseJSONAttribute(null, JSON.stringify(content));
          
          console.log(`DEBUG - Parsed content for ${metafieldKey}:`, parsedContent);
          
          if (parsedContent && parsedContent.length > 0) {
            tabsData[tabId] = {
              name: tabName,
              id: tabId,
              metafieldKey: metafieldKey,
              content: parsedContent,
              order: orderNumber // Agregar orden para sorting
            };
          }
        } else {
          console.log(`DEBUG - Skipping invalid/empty metafield: ${metafieldKey}`);
        }
      }
    });
    
    console.log('DEBUG - Final dynamic tabs:', tabsData);
    return tabsData;
  }

  extractOrderNumber(metafieldKey) {
    // Extraer número del final del metafield key (ej: "loom_tab_1" -> 1)
    const match = metafieldKey.match(/_tab_(\d+)$/);
    if (match) {
      return parseInt(match[1], 10);
    }
    
    // Si no tiene número, usar 999 para que aparezca al final
    return 999;
  }

  isValidMetafieldContent(content) {
    console.log('DEBUG - Validating content:', content, 'Type:', typeof content);
    
    // Verificar si el contenido es válido y no está vacío
    if (!content) {
      console.log('DEBUG - Content is null/undefined');
      return false;
    }
    
    // Si es null explícito (metafield eliminado)
    if (content === null) {
      console.log('DEBUG - Content is null (deleted metafield)');
      return false;
    }
    
    // Si es string vacío
    if (typeof content === 'string' && content.trim() === '') {
      console.log('DEBUG - Content is empty string');
      return false;
    }
    
    // Si es objeto (rich text de Shopify)
    if (typeof content === 'object') {
      // Verificar si es un objeto rich text válido de Shopify
      if (content.type === 'root' && content.children) {
        // Verificar si tiene contenido real (no solo estructura vacía)
        const hasRealContent = this.hasRealRichTextContent(content);
        console.log('DEBUG - Rich text has real content:', hasRealContent);
        return hasRealContent;
      }
      
      // Si es otro tipo de objeto, verificar si no está vacío
      const isValid = Object.keys(content).length > 0;
      console.log('DEBUG - Object is valid:', isValid);
      return isValid;
    }
    
    console.log('DEBUG - Content is valid (other type)');
    return true;
  }

  hasRealRichTextContent(richTextObj) {
    // Función recursiva para verificar si hay contenido real en el rich text
    if (!richTextObj || !richTextObj.children || richTextObj.children.length === 0) {
      console.log('DEBUG - No children in rich text object');
      return false;
    }
    
    for (const child of richTextObj.children) {
      console.log('DEBUG - Checking child:', child);
      
      if (child.type === 'text' && child.value && child.value.trim() !== '') {
        console.log('DEBUG - Found valid text content:', child.value.trim());
        return true;
      }
      
      if (child.type === 'paragraph' && child.children) {
        const hasContent = this.hasRealRichTextContent(child);
        if (hasContent) {
          return true;
        }
      }
      
      if (child.children && this.hasRealRichTextContent(child)) {
        return true;
      }
    }
    
    console.log('DEBUG - No real content found in rich text');
    return false;
  }

  generateTabName(metafieldKey) {
    // Convert metafield key to readable tab name
    return metafieldKey
      .replace(/_tab(_\d+)?$/, '') // Remove _tab or _tab_number suffix
      .replace(/_/g, ' ')          // Replace underscores with spaces
      .replace(/\b\w/g, l => l.toUpperCase()); // Capitalize each word
  }

  generateTabId(metafieldKey) {
    // Convert metafield key to tab ID
    return metafieldKey
      .replace(/_tab(_\d+)?$/, '') // Remove _tab or _tab_number suffix
      .replace(/_/g, '-');         // Replace underscores with dashes
  }

  parseJSONAttribute(attributeName, content = null) {
    const attributeValue = content || this.getAttribute(attributeName);
    if (attributeName) {
      console.log(`DEBUG - ${attributeName}:`, attributeValue);
    }
    
    if (!attributeValue) {
      if (attributeName) {
        console.log(`DEBUG - ${attributeName} is empty`);
      }
      return [];
    }
    
    // Primero intentar JSON (fallback para compatibilidad)
    try {
      const jsonResult = JSON.parse(attributeValue);
      console.log(`DEBUG - ${attributeName} parsed as JSON:`, jsonResult);
      
      // Solo manejar JSON de Shopify rich text (v2 metafields)
      if (jsonResult && jsonResult.type === 'root' && jsonResult.children) {
        console.log(`DEBUG - ${attributeName} is Shopify rich text, extracting...`);
        const extractedHTML = this.extractTextFromShopifyRichText(jsonResult);
        console.log(`DEBUG - ${attributeName} extracted HTML:`, extractedHTML);
        return this.parseRichTextContent(extractedHTML);
      }
      
      // Si no es rich text de Shopify, intentar como rich text directo
      console.log(`DEBUG - ${attributeName} unknown JSON format, trying as rich text...`);
      return this.parseRichTextContent(JSON.stringify(jsonResult));
    } catch (error) {
      // Si no es JSON, parsearlo como rich text estructurado
      console.log(`DEBUG - ${attributeName} parsing as rich text...`);
      const richTextResult = this.parseRichTextContent(attributeValue);
      console.log(`DEBUG - ${attributeName} parsed as rich text:`, richTextResult);
      return richTextResult;
    }
  }

  extractTextFromShopifyRichText(jsonObj) {
    let html = '';
    
    if (jsonObj.children && Array.isArray(jsonObj.children)) {
      for (const child of jsonObj.children) {
        if (child.type === 'paragraph' && child.children) {
          html += '<p>';
          for (const grandchild of child.children) {
            html += this.convertRichTextNode(grandchild);
          }
          html += '</p>';
        } else if (child.type === 'list' && child.children) {
          const listTag = child.listType === 'ordered' ? 'ol' : 'ul';
          html += `<${listTag}>`;
          for (const listItem of child.children) {
            if (listItem.type === 'list-item' && listItem.children) {
              html += '<li>';
              for (const itemChild of listItem.children) {
                html += this.convertRichTextNode(itemChild);
              }
              html += '</li>';
            }
          }
          html += `</${listTag}>`;
        }
      }
    }
    
    return html.trim();
  }

  convertRichTextNode(node) {
    if (node.type === 'text') {
      let text = node.value || '';
      
      // Aplicar formatos
      if (node.bold) {
        text = `<strong>${text}</strong>`;
      }
      if (node.italic) {
        text = `<em>${text}</em>`;
      }
      if (node.underline) {
        text = `<u>${text}</u>`;
      }
      if (node.strikethrough) {
        text = `<s>${text}</s>`;
      }
      
      return text;
    } else if (node.type === 'link' && node.url) {
      let linkText = '';
      if (node.children) {
        for (const child of node.children) {
          linkText += this.convertRichTextNode(child);
        }
      }
      return `<a href="${node.url}" target="_blank">${linkText}</a>`;
    }
    
    return '';
  }

  parseRichTextContent(content) {
    console.log('DEBUG - parseRichTextContent input:', content);
    // Parsear contenido rich text basado en estructura de líneas
    const products = [];
    
    // Si el contenido viene con tags HTML, procesarlo de manera diferente para tabs TEXT
    let hasHTMLTags = content.includes('<p>') || content.includes('<strong>') || content.includes('<em>') || content.includes('<ul>') || content.includes('<s>');
    let originalHTMLContent = content; // Guardar el HTML original
    
    if (hasHTMLTags) {
      // Para el parsing de estructura, limpiar HTML pero preservar saltos de línea
      content = content.replace(/<p>/g, '').replace(/<\/p>/g, '\n');
      content = content.replace(/<strong>/g, '**').replace(/<\/strong>/g, '**');
      content = content.replace(/<em>/g, '*').replace(/<\/em>/g, '*');
      content = content.replace(/<s>/g, '~~').replace(/<\/s>/g, '~~');
      content = content.replace(/<ul>/g, '\n').replace(/<\/ul>/g, '\n');
      content = content.replace(/<li>/g, '• ').replace(/<\/li>/g, '\n');
      content = content.replace(/<br\s*\/?>/g, '\n');
      console.log('DEBUG - Content after HTML cleanup:', content);
    }
    
    // Primero intentar parsear con saltos de línea
    let lines = content.split('\n').map(line => line.trim()).filter(line => line);
    console.log('DEBUG - Lines after split:', lines);
    
    // Si no hay saltos de línea (texto continuo), usar patrones para dividir
    if (lines.length <= 1) {
      console.log('DEBUG - Processing continuous text...');
      
      // Recrear líneas basándose en patrones conocidos
      content = content.replace(/(ROWS\s+[^\n]+)/g, '\n$1\n');    // Nuevo formato
      content = content.replace(/(TEXT\s+[^\n]+)/g, '\n$1\n');    // Nuevo formato
      content = content.replace(/(REMIX\s+[^\n]+)/g, '\n$1\n');   // Nuevo formato mixto
      content = content.replace(/(HYBRID\s+[^\n]+)/g, '\n$1\n');  // Nuevo formato mixto
      content = content.replace(/(---[^-]+---)/g, '\n$1\n');     // Compatibilidad
      content = content.replace(/(\/\/\/[^/]+\/\/\/)/g, '\n$1\n'); // Compatibilidad
      content = content.replace(/(~~~[^~]+~~~)/g, '\n$1\n');     // Compatibilidad
      // Agregar saltos de línea antes de separadores de contenido mixto
      content = content.replace(/(\[TEXT\])/g, '\n$1\n');
      content = content.replace(/(\[RICH\])/g, '\n$1\n');
      content = content.replace(/(\[ROWS\])/g, '\n$1\n');
      content = content.replace(/(\[TABLE\])/g, '\n$1\n');
      content = content.replace(/([A-Z0-9\s\-"]+)(Condition:)/g, '$1\n$2');
      content = content.replace(/(Condition:|Brand:|Chassis Type:|Form Factor:|Dimensions:|Processors:|Processor Sockets:|Memory:|Maximum RAM Speed:|RAID Controllers:|PCIe Slots:|I\/O Slots:|Hard Drives:|Max Storage:|Power Supplies:|Remote Management:|Rack Support:|Cooling Options:|Security Features:|Power Supply:|Remote Management:|Rack Support:|Warranty:)/g, '\n$1');
      content = content.replace(/([A-Z0-9\s\-"]+)(?=R250)/g, '$1\n');
      
      console.log('DEBUG - Content after processing:', content);
      lines = content.split('\n').map(line => line.trim()).filter(line => line);
      console.log('DEBUG - Lines after split:', lines);
    }
    
    let currentProduct = null;
    let currentSpecs = [];
    
    for (const line of lines) {
      console.log(`DEBUG - Processing line: "${line}"`);
      // Detectar nuevo producto (línea con ROWS, TEXT, REMIX, HYBRID o patrones específicos)
      // PERO NO tratar [TEXT] o [ROWS] como nuevos productos
      if ((line.startsWith('ROWS ') || 
          line.startsWith('TEXT ') ||
          line.startsWith('REMIX ') ||
          line.startsWith('HYBRID ') ||
          line.startsWith('~~~') && line.endsWith('~~~') ||  // Mantener compatibilidad
          line.startsWith('---') && line.endsWith('---') ||  // Mantener compatibilidad
          line.startsWith('///') && line.endsWith('///') ||  // Mantener compatibilidad
          line.startsWith('PRODUCT:') || 
          (line === line.toUpperCase() && !line.includes(':') && line.length > 3) ||
          line.match(/^R250\s+[\d\w\-\s"]+$/)) && 
          line !== '[TEXT]' && line !== '[RICH]' && line !== '[ROWS]' && line !== '[TABLE]') {
        
        // Guardar producto anterior si existe
        if (currentProduct) {
        if (currentProduct.isRichText) {
          // Para rich text, usar HTML original si está disponible
          let content;
          if (currentProduct.hasHTMLContent && currentProduct.originalHTML) {
            // Extraer solo el contenido del producto del HTML original
            content = currentProduct.originalHTML;
            // Remover el título del HTML si está presente
            const titlePattern = new RegExp(`(TEXT\\s+${currentProduct.title.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}|${currentProduct.title.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
            content = content.replace(titlePattern, '').trim();
            content = content.replace(/^<p>/, '').replace(/<\/p>$/, ''); // Limpiar tags p externos
          } else {
            content = currentProduct.content.join('');
          }
          
          products.push({
            productTitle: currentProduct.title,
            isRichText: true,
            richTextContent: content,
            hasHTMLContent: currentProduct.hasHTMLContent
          });
          } else if (currentProduct.isMixed) {
            // Finalizar la última sección
            this.finalizeMixedSection(currentProduct, currentSpecs);
            products.push({
              productTitle: currentProduct.title,
              isMixed: true,
              mixedSections: currentProduct.mixedSections || []
            });
          } else {
            products.push({
              productTitle: currentProduct.title,
              specifications: [...currentSpecs]
            });
          }
        }
        
        // Iniciar nuevo producto
        const cleanedTitle = line.replace('PRODUCT:', '')
                                .replace(/^ROWS\s+/, '')        // Nuevo formato
                                .replace(/^TEXT\s+/, '')        // Nuevo formato
                                .replace(/^REMIX\s+/, '')       // Nuevo formato mixto
                                .replace(/^HYBRID\s+/, '')      // Nuevo formato mixto
                                .replace(/^---\s*/, '')         // Compatibilidad
                                .replace(/\s*---$/, '')         // Compatibilidad
                                .replace(/^\/\/\/\s*/, '')      // Compatibilidad
                                .replace(/\s*\/\/\/$/, '')      // Compatibilidad
                                .replace(/^~~~\s*/, '')         // Compatibilidad
                                .replace(/\s*~~~$/, '')         // Compatibilidad
                                .trim();
        
        // Detectar el tipo de tab
        const isRichTextTab = line.startsWith('TEXT ') || (line.startsWith('///') && line.endsWith('///'));
        const isMixedTab = line.startsWith('REMIX ') || line.startsWith('HYBRID ') || (line.startsWith('~~~') && line.endsWith('~~~'));
        const isRowsTab = line.startsWith('ROWS ') || (line.startsWith('---') && line.endsWith('---'));
        
        currentProduct = {
          title: cleanedTitle,
          isRichText: isRichTextTab,
          isMixed: isMixedTab,
          content: (isRichTextTab || isMixedTab) ? [] : null,
          mixedSections: isMixedTab ? [] : null,
          hasHTMLContent: hasHTMLTags && isRichTextTab, // Marcar si tiene HTML y es TEXT
          originalHTML: hasHTMLTags && isRichTextTab ? originalHTMLContent : null
        };
        currentSpecs = [];
        
        console.log(`DEBUG - Created product: "${cleanedTitle}", isMixed: ${isMixedTab}, isRichText: ${isRichTextTab}`);
      }
      // Manejar contenido según el tipo de tab
      else if (currentProduct) {
        if (currentProduct.isRichText) {
          // Para tabs de rich text, agregar toda la línea como contenido
          currentProduct.content.push(line);
        } else if (currentProduct.isMixed) {
          console.log(`DEBUG - Processing mixed line: "${line}"`);
          // Para tabs mixtas, detectar separadores internos
          if (line === '[ROWS]' || line === '[TABLE]') {
            console.log('DEBUG - Switching to ROWS mode');
            // Finalizar sección anterior si existe
            this.finalizeMixedSection(currentProduct, currentSpecs);
            // Cambiar a modo rows
            currentProduct.currentSectionType = 'rows';
            currentProduct.currentSpecs = []; // Usar specs específicos del producto
          } else if (line === '[TEXT]' || line === '[RICH]') {
            console.log('DEBUG - Switching to TEXT mode');
            // Finalizar sección anterior si existe
            this.finalizeMixedSection(currentProduct, currentSpecs);
            // Cambiar a modo rich text
            currentProduct.currentSectionType = 'text';
            currentProduct.currentTextContent = [];
          } else if (currentProduct.currentSectionType === 'rows' && line.includes(':')) {
            // Procesar como especificación
            const [label, ...valueParts] = line.split(':');
            let value = valueParts.join(':').trim();
            
            if (value.includes(',') || value.includes('|')) {
              value = value.split(/[,|]/).map(v => v.trim()).filter(v => v);
            } else if (value.includes(' - ')) {
              value = value.split(' - ').map(v => v.trim()).filter(v => v);
            }
            
            if (!currentProduct.currentSpecs) currentProduct.currentSpecs = [];
            currentProduct.currentSpecs.push({
              label: label.trim(),
              value: value
            });
          } else if (currentProduct.currentSectionType === 'text') {
            console.log(`DEBUG - Adding text line: "${line}"`);
            // Agregar línea de texto
            if (!currentProduct.currentTextContent) currentProduct.currentTextContent = [];
            currentProduct.currentTextContent.push(line);
          } else {
            // Si no hay separador definido, tratar como texto por defecto
            if (!currentProduct.currentSectionType) {
              currentProduct.currentSectionType = 'text';
              currentProduct.currentTextContent = [];
            }
            if (!currentProduct.currentTextContent) currentProduct.currentTextContent = [];
            currentProduct.currentTextContent.push(line);
          }
        } else if (line.includes(':')) {
          // Para tabs de especificaciones normales
          const [label, ...valueParts] = line.split(':');
          let value = valueParts.join(':').trim();
          
          if (value.includes(',') || value.includes('|')) {
            value = value.split(/[,|]/).map(v => v.trim()).filter(v => v);
          } else if (value.includes(' - ')) {
            value = value.split(' - ').map(v => v.trim()).filter(v => v);
          }
          
          currentSpecs.push({
            label: label.trim(),
            value: value
          });
        }
      }
    }
    
    // Guardar último producto
    if (currentProduct) {
      if (currentProduct.isRichText) {
        // Para rich text, usar HTML original si está disponible
        let content;
        if (currentProduct.hasHTMLContent && currentProduct.originalHTML) {
          // Extraer solo el contenido del producto del HTML original
          content = currentProduct.originalHTML;
          // Remover el título del HTML si está presente
          const titlePattern = new RegExp(`(TEXT\\s+${currentProduct.title.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}|${currentProduct.title.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
          content = content.replace(titlePattern, '').trim();
          content = content.replace(/^<p>/, '').replace(/<\/p>$/, ''); // Limpiar tags p externos
        } else {
          content = currentProduct.content.join('');
        }
        
        products.push({
          productTitle: currentProduct.title,
          isRichText: true,
          richTextContent: content,
          hasHTMLContent: currentProduct.hasHTMLContent
        });
      } else if (currentProduct.isMixed) {
        // Finalizar la última sección
        this.finalizeMixedSection(currentProduct, currentSpecs);
        products.push({
          productTitle: currentProduct.title,
          isMixed: true,
          mixedSections: currentProduct.mixedSections || []
        });
      } else {
        products.push({
          productTitle: currentProduct.title,
          specifications: [...currentSpecs]
        });
      }
    }
    
    console.log('DEBUG - parseRichTextContent result:', products);
    return products;
  }

  finalizeMixedSection(currentProduct, currentSpecs) {
    if (!currentProduct.mixedSections) currentProduct.mixedSections = [];
    
    if (currentProduct.currentSectionType === 'rows') {
      const specsToUse = currentProduct.currentSpecs || currentSpecs || [];
      if (specsToUse.length > 0) {
        currentProduct.mixedSections.push({
          type: 'rows',
          specifications: [...specsToUse]
        });
        // Limpiar specs después de agregarlo
        if (currentProduct.currentSpecs) currentProduct.currentSpecs = [];
        if (currentSpecs) currentSpecs.length = 0;
      }
    } else if (currentProduct.currentSectionType === 'text' && currentProduct.currentTextContent && currentProduct.currentTextContent.length > 0) {
      currentProduct.mixedSections.push({
        type: 'text',
        content: currentProduct.currentTextContent.join('\n')
      });
      // Limpiar currentTextContent después de agregarlo
      currentProduct.currentTextContent = [];
    }
  }

  bindEvents() {
    this.buttons.forEach(button => {
      button.addEventListener('click', () => this.switchTab(button));
    });
  }

  switchTab(selectedButton) {
    const targetPanel = selectedButton.dataset.tab;

    this.buttons.forEach(button => {
      button.classList.toggle('active', button === selectedButton);
    });

    this.panels.forEach(panel => {
      panel.classList.toggle('active', panel.dataset.panel === targetPanel);
    });
  }

  renderDynamicTabs() {
    const tabsList = this.querySelector('.chassis-tabs__list');
    const tabsContent = this.querySelector('.chassis-tabs__content');
    
    // Ordenar tabs por número antes de renderizar
    const sortedTabs = Object.values(this.dynamicTabsData).sort((a, b) => {
      // Primero ordenar por número (order)
      if (a.order !== b.order) {
        return a.order - b.order;
      }
      // Si tienen el mismo orden, ordenar alfabéticamente
      return a.name.localeCompare(b.name);
    });
    
    console.log('DEBUG - Sorted tabs:', sortedTabs.map(tab => ({ name: tab.name, order: tab.order })));
    
    // Render each dynamic tab in sorted order
    sortedTabs.forEach(tabData => {
      if (tabData.content && tabData.content.length > 0) {
        // Create tab button
        const tabButton = document.createElement('button');
        tabButton.className = 'chassis-tabs__button';
        tabButton.setAttribute('data-tab', tabData.id);
        tabButton.textContent = tabData.name;
        tabsList.appendChild(tabButton);
        
        // Create tab panel
        const tabPanel = document.createElement('div');
        tabPanel.className = 'chassis-tabs__panel';
        tabPanel.setAttribute('data-panel', tabData.id);
        
        // Create content container
        const contentContainer = document.createElement('div');
        contentContainer.className = tabData.id;
        tabPanel.appendChild(contentContainer);
        
        // Render content
        this.renderTabContent(contentContainer, tabData.content);
        
        tabsContent.appendChild(tabPanel);
      }
    });
    
    // Re-bind events for new buttons
    this.buttons = this.querySelectorAll('.chassis-tabs__button');
    this.panels = this.querySelectorAll('.chassis-tabs__panel');
    this.bindEvents();
  }

  renderTabContent(container, data) {
    console.log('DEBUG - renderTabContent called:', container, data);
    if (!container) {
      console.log('DEBUG - No container found');
      return;
    }
    if (!data?.length) {
      console.log('DEBUG - No data or empty data:', data);
      return;
    }
    
    // Si hay un solo producto y es mixto, renderizarlo directamente sin tabs internas
    if (data.length === 1 && data[0].isMixed) {
      this.renderMixedContent(container, data[0].mixedSections);
      return;
    }
    
    // Create product tabs if multiple products exist
    if (data.length > 1) {
      const tabsContainer = document.createElement('div');
      tabsContainer.className = 'product-tabs';
      
      const tabsList = document.createElement('div');
      tabsList.className = 'product-tabs__list';
      
      const tabsContent = document.createElement('div');
      tabsContent.className = 'product-tabs__content';
      
      data.forEach((product, index) => {
        // Create tab button
        const tabButton = document.createElement('button');
        tabButton.className = `product-tabs__button ${index === 0 ? 'active' : ''}`;
        tabButton.textContent = product.productTitle;
        tabButton.dataset.productIndex = index;
        
        // Create tab content
        const tabContent = document.createElement('div');
        tabContent.className = `product-tabs__panel ${index === 0 ? 'active' : ''}`;
        
        // Add click event to tab button
        tabButton.addEventListener('click', () => {
          // Update active states
          tabsList.querySelectorAll('.product-tabs__button').forEach(btn => 
            btn.classList.toggle('active', btn === tabButton)
          );
          tabsContent.querySelectorAll('.product-tabs__panel').forEach(panel => 
            panel.classList.toggle('active', panel === tabContent)
          );
        });
        
        // Render content based on type
        if (product.isRichText) {
          this.renderRichTextContent(tabContent, product.richTextContent, product.hasHTMLContent);
        } else if (product.isMixed) {
          this.renderMixedContent(tabContent, product.mixedSections);
        } else {
        this.renderProductTable(tabContent, product.specifications);
        }
        
        // Append elements
        tabsList.appendChild(tabButton);
        tabsContent.appendChild(tabContent);
      });
      
      tabsContainer.appendChild(tabsList);
      tabsContainer.appendChild(tabsContent);
      container.appendChild(tabsContainer);
      
    } else if (data.length === 1) {
      // If only one product, render it directly
      const product = data[0];
      if (product.isRichText) {
        this.renderRichTextContent(container, product.richTextContent, product.hasHTMLContent);
      } else if (product.isMixed) {
        this.renderMixedContent(container, product.mixedSections);
      } else {
        this.renderProductTable(container, product.specifications);
      }
    }
  }

  renderProductTable(container, specifications) {
    specifications.forEach(item => {
      const row = document.createElement('div');
      row.className = 'spec-sheet__row';
      
      const label = document.createElement('div');
      label.className = 'spec-sheet__label';
      label.textContent = item.label;
      
      const value = document.createElement('div');
      value.className = 'spec-sheet__value';
      
      if (Array.isArray(item.value)) {
        item.value.forEach(val => {
          value.innerHTML += val + '<br>';
        });
      } else {
        value.textContent = item.value;
      }
      
      row.appendChild(label);
      row.appendChild(value);
      container.appendChild(row);
    });
  }

  renderRichTextContent(container, content, hasHTMLContent = false) {
    const richTextDiv = document.createElement('div');
    richTextDiv.className = 'rich-text-content';
    
    console.log('DEBUG - renderRichTextContent:', { content, hasHTMLContent });
    
    // Si tiene HTML original o contiene tags HTML, usarlo directamente
    if (hasHTMLContent || content.includes('<p>') || content.includes('<strong>') || content.includes('<em>') || content.includes('<ul>') || content.includes('<s>')) {
      richTextDiv.innerHTML = content;
    } else {
      // Si es texto plano, formatearlo mejor
      let formattedContent = content
        // Convertir saltos de línea a <br>
        .replace(/\n/g, '<br>')
        // Convertir markdown-style formatting
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        .replace(/~~(.*?)~~/g, '<s>$1</s>')
        // Detectar y formatear títulos (líneas que terminan con :)
        .replace(/^([^<\n]+:)$/gm, '<strong>$1</strong>')
        // Detectar listas con • o -
        .replace(/^[•\-]\s*(.+)$/gm, '<li>$1</li>')
        // Envolver listas consecutivas en <ul>
        .replace(/(<li>.*<\/li>)/gs, '<ul>$1</ul>')
        // Limpiar <ul> duplicados
        .replace(/<\/ul>\s*<ul>/g, '')
        // Agregar párrafos para bloques de texto
        .replace(/^([^<][^:\n]*[^>])$/gm, '<p>$1</p>');
      
      richTextDiv.innerHTML = formattedContent;
    }

    container.appendChild(richTextDiv);
  }

  renderMixedContent(container, mixedSections) {
    console.log('DEBUG - renderMixedContent called:', container, mixedSections);
    
    if (!mixedSections || mixedSections.length === 0) {
      console.log('DEBUG - No mixed sections to render');
      return;
    }
    
    mixedSections.forEach((section, index) => {
      console.log(`DEBUG - Rendering section ${index}:`, section);
      if (section.type === 'rows' && section.specifications) {
        // Renderizar tabla
        section.specifications.forEach(item => {
          const row = document.createElement('div');
          row.className = 'spec-sheet__row';
          
          const label = document.createElement('div');
          label.className = 'spec-sheet__label';
          label.textContent = item.label;
          
          const value = document.createElement('div');
          value.className = 'spec-sheet__value';
          
          if (Array.isArray(item.value)) {
            item.value.forEach(val => {
              value.innerHTML += val + '<br>';
            });
          } else {
            value.textContent = item.value;
          }
          
          row.appendChild(label);
          row.appendChild(value);
          container.appendChild(row);
        });
      } else if (section.type === 'text' && section.content) {
        // Renderizar rich text usando la función dedicada
        const textContainer = document.createElement('div');
        textContainer.style.marginBottom = '20px';
        this.renderRichTextContent(textContainer, section.content, section.hasHTMLContent);
        container.appendChild(textContainer);
      }
    });
  }
}

customElements.define('chassis-tabs', ChassisTabs); 